import { t } from '@lingui/macro'
import { useRouter } from 'next/router'
import React, { useEffect, useState } from 'react'

import { Button, Heading, Markdown, Space, Spinner, Stepper, Text } from '@/components'
import Account from '@/compositions/Apply/Account'
import Profile from '@/compositions/Apply/Profile'
import Registration from '@/compositions/Apply/Registration'
import RemoteImage from '@/compositions/RemoteImage'
import { Events } from '@/constants'
import { useCurrentUserLazyQuery, useGetApplicationFormQuery } from '@/graphql/generated'
import useTracking from '@/hooks/useTracking'
import { NextPageWithLayout } from '@/pages/_app'

const Apply: NextPageWithLayout = () => {
  const defaultSteps = [
    {
      name: t`Profile`,
      Component: Profile,
    },
    {
      name: t`Registration`,
      Component: Registration,
    },
  ]

  const {
    query: { slug },
    events,
  } = useRouter()
  const { trackEvent, setupPageViewTracking } = useTracking()
  const [getUser, { data: user }] = useCurrentUserLazyQuery()
  const { data: applicationForm } = useGetApplicationFormQuery({
    skip: !slug,
    variables: { learningCommunitySlug: slug?.toString() ?? '' },
    onCompleted: data => {
      trackEvent(Events.VISITED_APPLICATION_FORM, {
        learningCommunity: {
          id: data.applicationForm?.learningCommunity.id,
          name: data.applicationForm?.learningCommunity.name,
          slug,
        },
      })
    },
  })
  const [currentStep, setCurrentStep] = useState(1)
  const [steps, setSteps] = useState(defaultSteps)

  const nextStep = (): void => {
    setCurrentStep(currentStep + 1)

    trackEvent(`Proceed to ${steps[currentStep]?.name}`, {
      learningCommunity: {
        name: applicationForm?.applicationForm?.learningCommunity?.name,
        slug,
      },
    })
  }

  const prevStep = (): void => setCurrentStep(currentStep - 1)

  useEffect(() => {
    getUser()

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(setupPageViewTracking, [events, setupPageViewTracking])

  useEffect(() => {
    if (user?.me) {
      if (currentStep === 1) {
        setSteps(defaultSteps)
        setCurrentStep(2)
      }

      return
    }

    setSteps([
      ...defaultSteps,
      {
        name: t`Account`,
        Component: Account,
      },
    ])
    setCurrentStep(1)

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user])

  useEffect(() => {
    getUser()

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentStep])

  const componentProps = {
    applicationForm,
    user,
    nextStep,
    prevStep,
  }

  if (!applicationForm?.applicationForm)
    return (
      <div className="mx-auto flex h-screen items-center justify-center">
        <Spinner size="md" />
      </div>
    )

  const {
    title,
    body,
    heroImagePublicId,
    cohorts,
    learningCommunity: { name, creator, waitingListExternalUrl },
  } = applicationForm.applicationForm

  return (
    <div className="min-h-screen items-center bg-white sm:flex">
      <div className="mx-auto md:py-12 xl:w-5/12">
        <div className="bg-grey-bright px-8 py-12 md:rounded-xl md:p-16">
          <div className="text-center">
            {heroImagePublicId ? (
              <RemoteImage heroImagePublicId={heroImagePublicId} alt={creator?.fullName ?? ''} />
            ) : null}
            <div className="mt-6 space-y-2">
              <Heading level={2} variant={TextStyle => TextStyle.MEDIUM_2XL_NORMAL}>
                {name}
              </Heading>
              <Text className="text-grey-base">{creator?.fullName}</Text>
            </div>
          </div>

          <div className="mx-auto mt-12 w-16 border-t border-grey-light" />

          <div className="py-8">
            <div>
              <div className="rounded-xl bg-white p-8">
                <Space y={6}>
                  <div>
                    <Heading level={3} variant={TextStyle => TextStyle.MEDIUM_XL_NORMAL}>
                      {title}
                    </Heading>
                  </div>

                  <Space y={6}>
                    <Markdown>{body ?? ''}</Markdown>
                  </Space>
                </Space>
              </div>

              <Space y={12} />

              <div>
                <Space y={8}>
                  {cohorts.length === 0 && waitingListExternalUrl ? (
                    <div className="space-y-3 text-center">
                      <Text variant={TextStyle => TextStyle.MEDIUM_BASE_NORMAL}>
                        {t`Applications are currently closed`}
                      </Text>
                      <Button
                        className="justify-center"
                        href={waitingListExternalUrl}
                        variant="primary"
                        type="link"
                        newTab
                        excludeIcon
                      >
                        {t`Join the Waiting List`}
                      </Button>
                    </div>
                  ) : (
                    <Stepper list={steps.map(({ name }) => name)} currentStep={currentStep}>
                      {steps.map(({ Component }, index) => (
                        <Component {...componentProps} key={index} />
                      ))}
                    </Stepper>
                  )}
                </Space>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

Apply.getLayout = function getLayout(page: JSX.Element) {
  return page
}

export default Apply
